import { AnimatePresence, motion } from "motion/react";
import React, { useMemo } from "react";
import { ResearchId } from "../../adapters/insights-agent/state/AppState.ts";
import { Input } from "../../ui-library/Input/Input.tsx";
import { LoadingDots } from "../../ui-library/LoadingDots/LoadingDots.tsx";
import { HistoryItemMenu } from "./HistoryItemMenu.tsx";
import styles from "./HistorySidebar.module.css";
import { truncateOnWord } from "./truncateOnWord.ts";
import Fuse from "fuse.js";

export const HistorySidebar: React.FC<{
  items: {
    value: ResearchId;
    label: string;
    data?: { brief: string; questions: string };
  }[];
  onNewChat: () => void;
  onSelectionChange: (value: string) => void;
  onDelete?: (value: ResearchId) => void;
  onRename?: (value: ResearchId) => void;
  loading?: boolean;
  activeItem?: ResearchId;
  enableTermsAndConditions?: boolean;
  newChatLabel?: React.ReactNode;
  ContextMenuComponent?: React.FC<{ id: string }>;
}> = (props) => {
  const [searchValue, setSearchValue] = React.useState("");
  const fuse = useMemo(
    () =>
      new Fuse(props.items, {
        keys: ["label", "data.brief", "data.questions"],
      }),
    [props.items],
  );
  const items = useMemo(() => {
    if (searchValue) {
      const result = fuse.search(searchValue);
      return result.map((item) => item.item);
    }
    return props.items;
  }, [fuse, props.items, searchValue]);
  return (
    <nav className={styles.HistoryContainer}>
      <ul className={styles.HistoryList}>
        <h3 className={styles.HistoryTitle}>
          History{" "}
          {props.loading && (
            <>
              (loading
              <LoadingDots />)
            </>
          )}
        </h3>
        <div className={styles.SearchContainer}>
          <Input
            onChange={(e) => {
              setSearchValue(e.target.value);
            }}
            placeholder="Search"
          />
        </div>
        <AnimatePresence>
          {items.map(({ label, value }) => (
            <motion.li
              exit={{
                opacity: 0,
                height: 0,
                margin: 0,
                padding: 0,
                overflow: "hidden",
              }}
              className={
                styles.HistoryItem +
                " " +
                (props.activeItem === value ? styles.active : "")
              }
              key={value}
              onClick={() => props.onSelectionChange(value)}
            >
              {label && truncateOnWord(label, 90)}

              {props.ContextMenuComponent ? (
                <props.ContextMenuComponent id={value} />
              ) : (
                <HistoryItemMenu
                  onClickDelete={() => {
                    props.onDelete?.(value);
                  }}
                  onClickRename={() => {
                    props.onRename?.(value);
                  }}
                />
              )}
            </motion.li>
          ))}
        </AnimatePresence>
      </ul>
      <button className={styles.NewChatButton} onClick={props.onNewChat}>
        <p>{props.newChatLabel || "+ New Workflow"}</p>
      </button>
      <div>
        {props.enableTermsAndConditions && (
          <p className={styles.TermsAndConditions}>
            <a href="/terms-and-conditions" target="_blank">
              Terms and Conditions
            </a>
          </p>
        )}
      </div>
    </nav>
  );
};
